import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc, addDoc, collection, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { readStoredGuestRsvpToken, storeGuestRsvpToken } from '@/lib/guest-rsvp-token';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Header from '@/components/layout/Header';
import { EventPageBackground } from '@/components/layout/EventPageBackground';
import { formatDate, formatCurrency } from '@/lib/utils';
import { showError, showSuccess } from '@/lib/swal';
import { Calendar, MapPin, Ticket, XCircle } from 'lucide-react';
import { Event, Invoice, RSVP } from '@/types';
import { VenueInfo } from '@/components/venues/VenueInfo';
import { isActiveInvoiceData, matchesRsvpIdentity } from '@/lib/event-utils';
import { DEFAULT_CREDIT_BOOKING_FEE_PENCE } from '@/lib/platform-booking-fee';
import { PaymentSummaryCard, PaymentSummaryBreakdownRow } from '@/components/rsvp/PaymentSummaryCard';

export default function MyTicketsDashboardPage() {
  const { unitId, eventId, rsvpId } = useParams<{ unitId: string; eventId: string; rsvpId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<Event | null>(null);
  const [rsvp, setRsvp] = useState<RSVP | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [guestManageToken, setGuestManageToken] = useState<string | null>(null);
  const [verifyName, setVerifyName] = useState('');
  const [verifyEmail, setVerifyEmail] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const loadData = async () => {
    if (!unitId || !eventId || !rsvpId) {
      showError('Invalid link');
      setLoading(false);
      return;
    }

    try {
      // Guests (no account) load their booking through the token-verified getGuestRsvp callable —
      // RSVPs are no longer publicly readable. The capability token replaces the old sessionStorage
      // "identity verification". Authenticated owners/admins read directly.
      let rsvpData: RSVP;
      let guestInvoiceSummary: any = null;

      // A signed-in user may open a booking made as a guest under another email, which their account
      // can't read directly. If they have the emailed link, load it the way a guest would.
      let loadAsGuest = !currentUser;
      if (currentUser) {
        const linkToken = new URLSearchParams(location.search).get('t') || readStoredGuestRsvpToken(rsvpId);
        if (linkToken) {
          try {
            await getDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId));
          } catch (directErr) {
            loadAsGuest = (directErr as { code?: string })?.code === 'permission-denied';
          }
        }
      }

      if (loadAsGuest) {
        const guestToken =
          new URLSearchParams(location.search).get('t') || readStoredGuestRsvpToken(rsvpId);
        if (!guestToken) {
          showError('Please open your booking using the link we emailed you.');
          setLoading(false);
          return;
        }
        let payload: { rsvp: any; invoice: any } | null = null;
        try {
          const res: any = await httpsCallable(functions, 'getGuestRsvp')({
            unitId,
            eventId,
            rsvpId,
            token: guestToken,
          });
          payload = res?.data ?? null;
        } catch (guestErr) {
          console.error('Guest tickets load failed', guestErr);
          showError('This link is invalid or has expired. Please use the link from your email.');
          setLoading(false);
          return;
        }
        if (!payload?.rsvp) {
          showError('RSVP not found');
          setLoading(false);
          return;
        }
        storeGuestRsvpToken(rsvpId, guestToken);
        setGuestManageToken(guestToken);
        rsvpData = payload.rsvp as RSVP;
        guestInvoiceSummary = payload.invoice ?? null;
      } else {
        const signedInUser = currentUser!; // loadAsGuest is only false when signed in
        const rsvpDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId));
        if (!rsvpDoc.exists()) {
          showError('RSVP not found');
          setLoading(false);
          return;
        }

        const rawRsvp = rsvpDoc.data();
        rsvpData = {
          id: rsvpDoc.id,
          ...rawRsvp,
          createdAt: rawRsvp.createdAt?.toDate?.() ?? rawRsvp.createdAt,
          updatedAt: rawRsvp.updatedAt?.toDate?.() ?? rawRsvp.updatedAt,
          checkInTime: rawRsvp.checkInTime?.toDate?.() ?? rawRsvp.checkInTime,
        } as RSVP;

        if (
          rsvpData.userId &&
          rsvpData.userId.trim() !== '' &&
          signedInUser.uid !== rsvpData.userId &&
          !userProfile?.isAdmin
        ) {
          showError('You do not have permission to view this dashboard');
          navigate('/dashboard');
          return;
        }
      }

      const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
      if (eventDoc.exists()) {
        const rawEvent = eventDoc.data();
        const eventDate = rawEvent.date?.toDate() || new Date();
        setEvent({
          id: eventDoc.id,
          unitId,
          name: rawEvent.name,
          ceremonyType: rawEvent.ceremonyType,
          date: eventDate,
          location: rawEvent.location,
          venueId: rawEvent.venueId,
          questionnaire: rawEvent.questionnaire || [],
          courses: rawEvent.courses || [],
          tickets: rawEvent.tickets || [],
          ticketMode: rawEvent.ticketMode === 'multiTenant' ? 'multiTenant' : 'mealChoice',
          maxCapacity: rawEvent.maxCapacity,
          currentAttendees: rawEvent.currentAttendees || 0,
          waitlistEnabled: rawEvent.waitlistEnabled || false,
          waitlist: rawEvent.waitlist || [],
          rsvpDeadline: rawEvent.rsvpDeadline?.toDate() || new Date(),
          paymentDeadline: rawEvent.paymentDeadline?.toDate() || eventDate,
          eventStatus: rawEvent.eventStatus,
          dresscode: rawEvent.dresscode,
          customInstructions: rawEvent.customInstructions,
          isPublic: rawEvent.isPublic ?? false,
          logoUrl: rawEvent.logoUrl,
          bannerUrl: rawEvent.bannerUrl,
          backgroundImageUrl: rawEvent.backgroundImageUrl,
          backgroundColor: rawEvent.backgroundColor,
          createdBy: rawEvent.createdBy,
          createdAt: rawEvent.createdAt?.toDate() || new Date(),
          guestCoversCreditCost: rawEvent.guestCoversCreditCost ?? false,
          creditBookingFeePence:
            typeof rawEvent.creditBookingFeePence === 'number' ? rawEvent.creditBookingFeePence : undefined,
          creditCostRecovery:
            rawEvent.creditCostRecovery && typeof rawEvent.creditCostRecovery === 'object' && !Array.isArray(rawEvent.creditCostRecovery)
              ? {
                  enabled: Boolean((rawEvent.creditCostRecovery as { enabled?: boolean }).enabled),
                  amountPence:
                    typeof (rawEvent.creditCostRecovery as { amountPence?: unknown }).amountPence === 'number'
                      ? (rawEvent.creditCostRecovery as { amountPence: number }).amountPence
                      : DEFAULT_CREDIT_BOOKING_FEE_PENCE,
                }
              : { enabled: false, amountPence: DEFAULT_CREDIT_BOOKING_FEE_PENCE },
        });
      }

      if (loadAsGuest) {
        // Guests receive a non-PII invoice summary from getGuestRsvp (they can't read invoices directly).
        if (guestInvoiceSummary) {
          setInvoice({
            ...(guestInvoiceSummary as Invoice),
            createdAt: new Date(),
            updatedAt: new Date(),
            remindersSent: [],
            paymentHistory: [],
          } as Invoice);
        }
      } else if (rsvpData.invoiceId) {
        const invoiceDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId, 'invoices', rsvpData.invoiceId));
        if (invoiceDoc.exists() && isActiveInvoiceData(invoiceDoc.data())) {
          const rawInvoice = invoiceDoc.data();
          setInvoice({
            id: invoiceDoc.id,
            ...rawInvoice,
            createdAt: rawInvoice.createdAt?.toDate(),
            updatedAt: rawInvoice.updatedAt?.toDate(),
            remindersSent: rawInvoice.remindersSent?.map((d: any) => d.toDate()) || [],
            paymentHistory:
              rawInvoice.paymentHistory?.map((p: any) => ({ ...p, date: p.date?.toDate() })) || [],
          } as Invoice);
        }
      }

      setRsvp(rsvpData);
      setNeedsVerification(false);
    } catch (error) {
      console.error('Error loading tickets dashboard:', error);
      showError('Failed to load your tickets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId, eventId, rsvpId, currentUser]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rsvp || !rsvpId) return;
    setVerifying(true);
    try {
      const matches = matchesRsvpIdentity(
        { name: verifyName, email: verifyEmail, rank: '' },
        rsvp,
        { requiresPrefix: false },
      );
      if (!matches) {
        showError('Those details do not match our records. Please try again.');
        return;
      }
      sessionStorage.setItem(`rsvp_verified_${rsvpId}`, 'true');
      showSuccess('Verified!');
      setLoading(true);
      await loadData();
    } finally {
      setVerifying(false);
    }
  };

  const handlePayNow = () => {
    if (!invoice) return;
    navigate(`/payment/${invoice.id}`);
  };

  const notifyUnitOfCancellation = async () => {
    if (!unitId || !rsvp || !event) return;
    try {
      const unitDoc = await getDoc(doc(db, 'units', unitId));
      if (!unitDoc.exists()) return;

      const members = (unitDoc.data().members || []) as Array<{ userId?: string; role?: string }>;
      const adminMembers = members.filter(
        (member) => member.role === 'admin' || member.role === 'secretary' || member.role === 'treasurer',
      );

      await Promise.all(
        adminMembers
          .filter((member) => !!member.userId)
          .map((member) =>
            addDoc(collection(db, 'users', member.userId as string, 'notifications'), {
              type: 'rsvp_cancelled',
              title: 'Ticket booking cancelled',
              message: `${rsvp.name} has cancelled their ticket booking for "${event.name}"${
                cancellationReason ? `: ${cancellationReason}` : ''
              }`,
              eventId,
              unitId,
              rsvpId,
              read: false,
              createdAt: Timestamp.now(),
            }),
          ),
      );
    } catch (error) {
      console.error('Error notifying unit of ticket cancellation:', error);
    }
  };

  const handleSubmitCancellation = async () => {
    if (!unitId || !eventId || !rsvpId || !rsvp) return;

    setCancelling(true);
    try {
      // Guests route the cancellation through the token-verified callable; owners write directly.
      if (guestManageToken) {
        await httpsCallable(functions, 'cancelGuestRsvp')({
          unitId,
          eventId,
          rsvpId,
          token: guestManageToken,
          reason: cancellationReason.trim(),
        });
      } else {
        await updateDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId), {
          status: 'cancelled',
          cancellationReason: cancellationReason.trim() || null,
          cancelledAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }

      await notifyUnitOfCancellation();

      setRsvp({ ...rsvp, status: 'cancelled' } as RSVP);
      showSuccess('Your tickets have been cancelled. The unit has been notified.');
      setShowCancelModal(false);
      setCancellationReason('');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error cancelling tickets:', error);
      showError('Failed to cancel your tickets. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading your tickets...</p>
          </div>
        </div>
      </div>
    );
  }

  if (needsVerification) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center px-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Verify Your Identity</CardTitle>
              <CardDescription>Enter the details you used when purchasing tickets to view your dashboard.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="verifyName">Full Name</Label>
                  <Input id="verifyName" value={verifyName} onChange={(e) => setVerifyName(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="verifyEmail">Email Address</Label>
                  <Input id="verifyEmail" type="email" value={verifyEmail} onChange={(e) => setVerifyEmail(e.target.value)} required />
                </div>
                <Button type="submit" className="w-full" disabled={verifying}>
                  {verifying ? 'Verifying...' : 'Verify'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!event || !rsvp) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Not Found</CardTitle>
              <CardDescription>This ticket dashboard could not be found.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/')}>Return Home</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const isCancelled = rsvp.status === 'cancelled' || rsvp.status === 'declined';
  const ticketLines: Array<{ id: string; name: string; price: number; qty: number }> = rsvp.responses?.Tickets || [];
  const totalQuantity = ticketLines.reduce((sum, t) => sum + (t.qty || 1), 0);
  const displayTotal = invoice?.totalAmount ?? ticketLines.reduce((sum, t) => sum + t.price * (t.qty || 1), 0);
  const amountPaid = invoice?.amountPaid ?? 0;
  const outstandingBalance = invoice?.outstandingBalance ?? Math.max(0, displayTotal - amountPaid);

  const breakdownRows: PaymentSummaryBreakdownRow[] = ticketLines.map((t) => ({
    key: t.id,
    label: `${t.name} x${t.qty}`,
    ticketDescription: `${formatCurrency(t.price)} each`,
    ticketPrice: t.price,
    bookingFee: 0,
    lineTotal: t.price * (t.qty || 1),
    paidBadge: invoice ? (invoice.status === 'paid' ? 'paid' : 'pending') : null,
  }));

  return (
    <EventPageBackground backgroundImageUrl={event.backgroundImageUrl} backgroundColor={event.backgroundColor}>
      <Header />
      <main className="flex-1 px-3 py-8 sm:px-6">
        <div className="w-full sm:mx-auto sm:max-w-3xl space-y-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <CardTitle className="text-2xl">{event.name}</CardTitle>
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center text-base">
                      <Calendar className="mr-2 h-4 w-4" />
                      {formatDate(event.date)}
                    </div>
                    <div className="flex items-start text-base">
                      <MapPin className="mr-2 h-4 w-4 mt-0.5 flex-shrink-0" />
                      <VenueInfo unitId={unitId!} venueId={event.venueId} location={event.location} compact={true} />
                    </div>
                  </div>
                </div>
                <Badge variant={isCancelled ? 'destructive' : rsvp.status === 'confirmed' ? 'default' : 'secondary'}>
                  {rsvp.status}
                </Badge>
              </div>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="h-5 w-5" />
                Your Tickets
              </CardTitle>
              <CardDescription>
                {rsvp.firstName} {rsvp.lastName} &middot; {totalQuantity} ticket{totalQuantity === 1 ? '' : 's'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {ticketLines.length === 0 ? (
                <p className="text-sm text-muted-foreground">No tickets found for this booking.</p>
              ) : (
                ticketLines.map((t) => (
                  <div key={t.id} className="flex items-center justify-between rounded-lg border p-3">
                    <p className="font-medium">{t.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {t.qty} x {formatCurrency(t.price)} = {formatCurrency(t.price * (t.qty || 1))}
                    </p>
                  </div>
                ))
              )}
              {isCancelled ? (
                <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                  <XCircle className="h-4 w-4 shrink-0" />
                  This booking has been cancelled. Your tickets are no longer valid.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/edit-tickets`)}
                  >
                    Edit Ticket Quantities
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="gap-2"
                    onClick={() => setShowCancelModal(true)}
                  >
                    <XCircle className="h-4 w-4" />
                    Cancel Booking
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {invoice && (
            <PaymentSummaryCard
              invoice={invoice}
              totalGuests={totalQuantity}
              displayTotal={displayTotal}
              amountPaid={amountPaid}
              outstandingBalance={outstandingBalance}
              breakdownRows={breakdownRows}
              onPayNow={handlePayNow}
            />
          )}
        </div>
      </main>

      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              Cancel Booking
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your ticket booking for "{event.name}"? This will remove your
              tickets and registration. The unit will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="cancellation-reason">Reason for cancellation (optional)</Label>
              <Textarea
                id="cancellation-reason"
                placeholder="Please provide a reason for cancelling..."
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowCancelModal(false);
                setCancellationReason('');
              }}
              disabled={cancelling}
            >
              Keep Booking
            </Button>
            <Button type="button" variant="destructive" onClick={handleSubmitCancellation} disabled={cancelling}>
              {cancelling ? 'Cancelling...' : 'Cancel Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </EventPageBackground>
  );
}
